import type { User } from '@repo-level/core-front';
import { LEVELS, PROGRESS_STEPS, STATUS_GROUP_PREFIX } from '../constants';
import type { ELevelKey, EProgressStep, IStatusGroup } from '../types';

const GROUP_PATTERN = new RegExp(`^${STATUS_GROUP_PREFIX}_([a-z_]+)_(\\d+)$`);

const levelOrderByKey = new Map<string, number>(
  LEVELS.map(({ key, order }) => [key, order])
);

const steps = PROGRESS_STEPS as readonly number[];

const isLevelKey = (value: string): value is ELevelKey =>
  levelOrderByKey.has(value);

const isProgressStep = (value: number): value is EProgressStep =>
  steps.includes(value);

const parseGroupId = (statusId: string): IStatusGroup | null => {
  const match = GROUP_PATTERN.exec(statusId.toLowerCase());

  if (!match) return null;

  const [, levelKey, rawStep] = match;
  const step = Number.parseInt(rawStep, 10);

  if (!(isLevelKey(levelKey) && isProgressStep(step))) return null;

  return { levelKey, step };
};

const isFurther = (group: IStatusGroup, than: IStatusGroup): boolean => {
  const orderDiff =
    (levelOrderByKey.get(group.levelKey) ?? 0) -
    (levelOrderByKey.get(than.levelKey) ?? 0);

  return orderDiff === 0 ? group.step > than.step : orderDiff > 0;
};

export const resolveStatusGroup = (user?: User): IStatusGroup | null =>
  user?.statuses?.reduce<IStatusGroup | null>((furthest, { id }) => {
    const group = parseGroupId(id);

    if (!group) return furthest;

    return !furthest || isFurther(group, furthest) ? group : furthest;
  }, null) ?? null;
